/**
 * Residence Form Component
 * Wrapper component that renders the appropriate residence form based on type
 */

import type { ReactNode } from 'react'
import { RentForm } from './RentForm'
import { OwnForm } from './OwnForm'
import { FamilyForm } from './FamilyForm'
import type { ResidenceType } from '@/features/applicant/types/address'
import type { RentFormErrors, OwnFormErrors, FamilyFormErrors } from './types'

interface ResidenceFormProps {
  residenceType: ResidenceType
  isReadOnly?: boolean
  isPreviousAddress?: boolean
  rentAccessory?: ReactNode
  // Rent props
  rentProps?: {
    landlordName: string
    landlordEmail: string
    landlordMobile: string
    moveInDate: string
    leaseEndDate: string
    rentAmount: string
    monthsPaid?: string
    stillHere: boolean
    onLandlordNameChange: (value: string) => void
    onLandlordEmailChange: (value: string) => void
    onLandlordMobileChange: (value: string) => void
    onMoveInDateChange: (value: string) => void
    onLeaseEndDateChange: (value: string) => void
    onRentAmountChange: (value: string) => void
    onMonthsPaidChange?: (value: string) => void
    onStillHereChange: (value: boolean) => void
    errors?: RentFormErrors
  }
  // Own props
  ownProps?: {
    moveInDate: string
    plannedMoveOutDate: string
    monthlyMortgagePayment: string
    monthsResided: string
    stillHere: boolean
    onMoveInDateChange: (value: string) => void
    onPlannedMoveOutDateChange: (value: string) => void
    onMonthlyMortgagePaymentChange: (value: string) => void
    onMonthsResidedChange: (value: string) => void
    onStillHereChange: (value: boolean) => void
    errors?: OwnFormErrors
  }
  // Family props
  familyProps?: {
    contactName: string
    contactRelationship: string
    contactEmail: string
    contactMobile: string
    contactWebsite: string
    moveInDate: string
    plannedMoveOutDate: string
    rentAmount: string
    monthsPaid?: string
    stillHere: boolean
    onContactNameChange: (value: string) => void
    onContactRelationshipChange: (value: string) => void
    onContactEmailChange: (value: string) => void
    onContactMobileChange: (value: string) => void
    onContactWebsiteChange: (value: string) => void
    onMoveInDateChange: (value: string) => void
    onPlannedMoveOutDateChange: (value: string) => void
    onRentAmountChange: (value: string) => void
    onMonthsPaidChange?: (value: string) => void
    onStillHereChange: (value: boolean) => void
    errors?: FamilyFormErrors
  }
}

export function ResidenceForm({
  residenceType,
  isReadOnly = false,
  isPreviousAddress = false,
  rentAccessory,
  rentProps,
  ownProps,
  familyProps,
}: ResidenceFormProps) {
  if (residenceType === 'rent' && rentProps) {
    return (
      <RentForm
        {...rentProps}
        isReadOnly={isReadOnly}
        isPreviousAddress={isPreviousAddress}
        accessory={rentAccessory}
      />
    )
  }

  if (residenceType === 'own' && ownProps) {
    return <OwnForm {...ownProps} isReadOnly={isReadOnly} isPreviousAddress={isPreviousAddress} />
  }

  if (residenceType === 'family' && familyProps) {
    return <FamilyForm {...familyProps} isReadOnly={isReadOnly} isPreviousAddress={isPreviousAddress} />
  }

  return null
}

